import React, { useContext, useMemo } from 'react'
import PaginationResultsBlock from '@Components/Navigations/PaginationResultsBlock'
import { getNumTitles } from '@App/helpers/pagination/get-num-titles'
import Table, { TableRowData } from '@Components/ViewWork/Table'
import PaginationAndLimit from '@Components/Navigations/PaginationAndLimit'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import { CatalogTransferView } from '@Entities/rightsholder/catalog-transfer-view'
import { buildTableRows } from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferList/build-table-rows'
import { PaginationBody } from '@Entities/pagination'
import { observer } from 'mobx-react'
import SearchPanel from '@Components/Search/SearchPanel'
import {
    CatalogTransferFilter,
    CatalogTransferFilterCollection,
    catalogTransferSortOptions,
} from '@App/forms/filters/catalog-transfer-filter'
import { CatalogTransferSearchQuery } from '@App/stores/search/models/catalog-transfer-search'
import CatalogTransferTabStore from '@App/stores/rightsholder/catalogTransfer/catalog-transfer-tab-store'
import { StoresContext } from '@App/contexts'
import {
    getMyPublisher,
    getDateHeader,
    getDateValue,
    getTheirPublisher,
} from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferList/CatalogTransferTable/utils/catalog-transfer-table-utils'
import {
    CatalogTransferFilterFormValues,
    useCatalogTransferFilters,
} from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferList/CatalogTransferTable/hooks/useCatalogTransferFilters'
import { useCatalogTransferSearch } from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferList/CatalogTransferTable/hooks/useCatalogTransferSearch'
import { useCatalogTransferSearchTerms } from '@Scenes/RightsholderScene/CatalogTransfer/CatalogTransferList/CatalogTransferTable/hooks/useCatalogTransferSearchTerms'

interface CatalogTransferTableProps {
    onDeletedClicked?: (transfer: CatalogTransferView) => void
    isReviewTab?: boolean
    catalogTransferTabStore: CatalogTransferTabStore
}
const CatalogTransferTable = ({
    onDeletedClicked,
    isReviewTab,
    catalogTransferTabStore,
}: CatalogTransferTableProps) => {
    const { rightsholderStore, userStore } = useContext(StoresContext)
    const { desiredFilter, normalizeFilterValues, syncFiltersToUrl } = useCatalogTransferFilters(
        catalogTransferTabStore,
        isReviewTab
    )
    const { syncCriteriaToUrl } = useCatalogTransferSearchTerms(catalogTransferTabStore, isReviewTab)
    const isLoading = useCatalogTransferSearch(catalogTransferTabStore).isFetching

    const handleSearch = (
        searchCriteria: Partial<CatalogTransferSearchQuery>,
        filter: Partial<CatalogTransferFilter>
    ) => {
        if (isReviewTab) {
            syncCriteriaToUrl(searchCriteria)
        }
        catalogTransferTabStore.startSearch(searchCriteria, filter)
    }

    const onPageChange = (pagination?: PaginationBody) => {
        catalogTransferTabStore.setCurrentPage(pagination?.page ?? 1)
    }

    const reset = () => {
        catalogTransferTabStore.reset()
    }

    const { paginationInfo, criteriaStore, catalogTransferData, limit } = catalogTransferTabStore
    const totalTransfers = catalogTransferData?.totalElements || 0
    const currentPage = paginationInfo ? paginationInfo.currentPage : 1
    const currentRightsholderId = rightsholderStore.currentRightsholderId
    const isInternal = userStore.isInternal

    const transferRows: TableRowData[] | undefined = useMemo(
        () =>
            buildTableRows(catalogTransferData?.content, {
                getMyPublisher,
                getTheirPublisher,
                getDateHeader,
                getDateValue,
                onDeletedClicked,
                rightsholderId: currentRightsholderId,
                isReviewTab,
                isDisabled: isInternal,
            }),
        [catalogTransferData, currentRightsholderId, onDeletedClicked, isReviewTab]
    )

    // Called when the user explicitly applies filters (Apply Filters button). This should
    // update the current filter and perform the search.
    const handleApplyFilters = (filters: CatalogTransferFilterFormValues) => {
        catalogTransferTabStore.applyFilters(normalizeFilterValues(filters))
    }

    return (
        <>
            <section className="showing-search">
                <div className="showing-search-right">
                    {criteriaStore && (
                        <SearchPanel
                            searchCriteriaStore={criteriaStore}
                            onSearch={handleSearch}
                            disabled={isLoading}
                            onReset={() => {
                                if (isReviewTab) {
                                    syncFiltersToUrl(undefined)
                                    syncCriteriaToUrl(undefined)
                                }
                                reset()
                            }}
                            filterFieldCollections={CatalogTransferFilterCollection}
                            onApplyFilters={handleApplyFilters}
                            onFilterValueChange={isReviewTab ? syncFiltersToUrl : undefined}
                            disableAutoComplete
                            defaultFilterValues={desiredFilter}
                            sortOptions={catalogTransferSortOptions}
                            sortFilterKey="sort_by"
                        />
                    )}
                </div>
            </section>
            {!isLoading ? (
                <>
                    <PaginationResultsBlock
                        totalResults={totalTransfers}
                        hideNoResults
                        workNumTitles={getNumTitles(totalTransfers, currentPage, limit)}
                        resultsMessage="Catalog Transfers Found"
                        showCompactToggle={false}
                    />
                    {transferRows && <Table rows={transferRows} />}
                    <PaginationAndLimit
                        count={totalTransfers}
                        limit={limit}
                        paginationInfo={paginationInfo}
                        onPageChange={onPageChange}
                    />
                </>
            ) : (
                <PointPreloader />
            )}
        </>
    )
}

export default observer(CatalogTransferTable)
